// Contract 29 §6.1–6.4 + §4 — the feature's only `appHttp` importer for the onboarding journey.
// Every endpoint is grounded in a legacy call site; none of them is called from a component.

import type {
  BrandContextRequest,
  CreateWorkspaceResponse,
  HeardAboutSource,
  WorkspaceBrandDefaults,
  OnboardingLiveAds,
} from '../models/onboarding.types'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

export class OnboardingApi {
  /** §6.1 — `POST /accounts/{accountId}/workspace/create/`. 4xx aborts the step. */
  async createWorkspace(accountId: number, name: string, mcpOnly = false): Promise<number | null> {
    const response = await appHttp.post<CreateWorkspaceResponse>(
      `/accounts/${accountId}/workspace/create/`,
      { name, account_id: accountId, mcp_only: mcpOnly },
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
    return unwrap(response)?.workspace ?? null
  }

  /** §6.2 — seeds step 2. Non-fatal: a rejection must not fail workspace creation. */
  async brandContextProfile(
    accountId: number,
    payload: BrandContextRequest,
  ): Promise<WorkspaceBrandDefaults | null> {
    const response = await appHttp.post<ApiEnvelope<WorkspaceBrandDefaults>>(
      `/accounts/${accountId}/workspace/brand-context/profile/`,
      payload,
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
    return unwrapEnvelope(response) ?? null
  }

  /**
   * The first-run brand proof uses the same account discovery endpoint as the Meta connector.
   * Keep this read in the onboarding API so the step has a real loading/error state instead of a
   * decorative placeholder. The backend has returned both a bare array and an enveloped list.
   */
  async getLiveMetaAds(workspaceId: number | string): Promise<OnboardingLiveAds> {
    const response = await appHttp.get<unknown>(
      `/integrations/${workspaceId}/get_facebook_ad_accounts`,
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
    const payload = unwrap(response)
    const rowsFrom = (value: unknown): unknown[] => {
      if (Array.isArray(value)) return value
      if (!value || typeof value !== 'object') return []
      const record = value as Record<string, unknown>
      return rowsFrom(record.accounts ?? record.ad_accounts ?? record.data)
    }
    const accounts = rowsFrom(payload)
    const payloadRecord =
      payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {}
    const countValue = Number(payloadRecord.count ?? payloadRecord.total ?? accounts.length)
    return {
      count: Number.isFinite(countValue) ? Math.max(0, Math.trunc(countValue)) : accounts.length,
      previews: accounts.slice(0, 3).flatMap((value, index) => {
        if (!value || typeof value !== 'object') return []
        const row = value as Record<string, unknown>
        const id = String(row.id ?? row.account_id ?? row.facebook_ad_account_id ?? index)
        return [
          {
            id,
            name: String(row.name ?? row.account_name ?? id),
            imageUrl:
              typeof row.image_url === 'string'
                ? row.image_url
                : typeof row.thumbnail_url === 'string'
                  ? row.thumbnail_url
                  : null,
          },
        ]
      }),
    }
  }

  /** §6.3 — fire-and-forget provisioning; the caller routes the rejection to a reporter. */
  async provisionServiceAccount(workspaceId: number | string): Promise<void> {
    await appHttp.post(`/integrations/${workspaceId}/service_account_integration`, undefined, {
      suppressGlobalErrorHandler: true,
      meta: { suppressGlobalErrorHandler: true },
    })
  }

  /** §6.4 — the settings half of step 2. Only `timezone` travels this way today. */
  async updateWorkspaceSettings(
    accountId: number,
    workspaceId: number | string,
    body: Record<string, string>,
  ): Promise<void> {
    await appHttp.post(`/accounts/${accountId}/workspaces/${workspaceId}/settings/update/`, body, {
      suppressGlobalErrorHandler: true,
      meta: { suppressGlobalErrorHandler: true },
    })
  }

  async renameAccount(accountId: number, name: string): Promise<void> {
    await appHttp.patch(
      `/accounts/${accountId}/change-account-name/`,
      { new_account_name: name },
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
  }

  /** v3 `OnboardingWorkspaceSettings.vue:331` — the business type lives on the account's pricing
   *  record, not on the workspace: `PATCH /accounts/{a}/pricing/business-type/`. */
  async saveBusinessType(accountId: number, value: string): Promise<void> {
    await appHttp.patch(
      `/accounts/${accountId}/pricing/business-type/`,
      { business_type: value },
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
  }

  /** The saved business type, for the step's pre-selection (v3 `:398`). Absent is `null`. */
  async getBusinessType(accountId: number): Promise<string | null> {
    const response = await appHttp.get<{ data?: { business_type?: string | null } }>(
      `/accounts/${accountId}/pricing/business-type/`,
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
    const body = unwrap(response) as { data?: { business_type?: string | null } } | null
    return body?.data?.business_type ?? null
  }

  async getHeardAbout(
    accountId: number,
  ): Promise<{ source: HeardAboutSource | null; other: string }> {
    const response = await appHttp.get<{
      heard_about_source?: HeardAboutSource
      heard_about_other?: string
    }>(`/accounts/${accountId}/heard-about/`)
    const body = unwrap(response)
    return { source: body?.heard_about_source ?? null, other: body?.heard_about_other ?? '' }
  }

  async saveHeardAbout(accountId: number, source: HeardAboutSource, other = ''): Promise<void> {
    await appHttp.patch(
      `/accounts/${accountId}/heard-about/`,
      {
        heard_about_source: source,
        heard_about_other: source === 'Something else' ? other.trim() || null : null,
      },
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
  }
}
